import { useEffect, useState } from 'react';
import { Briefcase, CheckCircle, AlertCircle, Users, TrendingUp, AlertTriangle, Clock } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { getJobs, getJob, getJobsStats } from '../api/jobs';
import { getFieldStaff } from '../api/users';
import { StatusBadge, PriorityBadge } from '../components/jobs/StatusBadge';
import { JobDetail } from '../components/jobs/JobDetail';
import { JobForm } from '../components/jobs/JobForm';
import { Modal } from '../components/ui/Modal';
import { format } from 'date-fns';
import { tr } from 'date-fns/locale';
import { formatJobNumber } from '../utils/constants';

function StatCard({ icon: Icon, label, value, color, sub, onClick }) {
  return (
    <div
      onClick={onClick}
      className={`bg-white rounded-xl p-5 border border-slate-100 shadow-sm transition ${onClick ? 'cursor-pointer hover:border-slate-300 hover:shadow-md' : ''}`}
    >
      <div className="flex items-center justify-between mb-3">
        <div className={`p-2 rounded-lg ${color}`}>
          <Icon size={20} className="text-white" />
        </div>
        {sub !== undefined && (
          <span className="text-xs text-slate-400">{sub}</span>
        )}
      </div>
      <p className="text-2xl font-bold text-slate-800">{value}</p>
      <p className="text-sm text-slate-500 mt-0.5">{label}</p>
    </div>
  );
}

/**
 * Kullanıcı tarayıcı yerel günü için bugünün başlangıcını ISO döner.
 * Sprint 2.8.6 T7-B KARAR 2 şartı: backend CURRENT_DATE fallback'ine
 * GÜVENİLMEYECEK — TRT 00:00 UTC'ye normalize edilip her zaman gönderilir.
 */
function todayStartIso() {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d.toISOString();
}

export function DashboardPage() {
  const navigate = useNavigate();

  // Sprint 2.8.6 T7-B: jobs[] monolith state KALDIRILDI.
  // Sayılar /api/jobs/stats'tan (COUNT FILTER); listeler ayrı filtered çağrılar.
  const [stats, setStats] = useState(null);
  const [topStaff, setTopStaff] = useState(null);
  const [activeList, setActiveList] = useState([]);
  const [recentList, setRecentList] = useState([]);
  const [staffCount, setStaffCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [detailJob, setDetailJob] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [editJob, setEditJob] = useState(null);

  const loadDashboard = () => {
    setLoading(true);
    Promise.all([
      getJobsStats({ today_start: todayStartIso() }),
      getJobs({ status: 'assigned,on_the_way,started', limit: 50 }),
      getJobs({ limit: 8 }),
      getFieldStaff(),
    ]).then(([statsData, activeData, recentData, staffData]) => {
      setStats(statsData.stats);
      setTopStaff(statsData.top_staff);
      setActiveList(activeData.jobs);
      setRecentList(recentData.jobs);
      setStaffCount(staffData.users.length);
    }).catch((err) => {
      console.error('Dashboard yüklenemedi:', err);
    }).finally(() => setLoading(false));
  };

  useEffect(() => { loadDashboard(); }, []);

  const openDetail = async (job) => {
    setDetailLoading(true);
    try {
      const data = await getJob(job.id);
      setDetailJob(data.job);
    } catch {
      setDetailJob(job);
    } finally {
      setDetailLoading(false);
    }
  };

  if (loading || !stats) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  const overdueTotal = stats.overdue_unassigned + stats.overdue_assigned;

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-slate-800">Dashboard</h1>
        <p className="text-slate-500 text-sm mt-0.5">
          {format(new Date(), 'd MMMM yyyy, EEEE', { locale: tr })}
        </p>
      </div>

      {/* Gecikme Uyarıları */}
      {stats.overdue_unassigned > 0 && (
        <div className="mb-4 flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 rounded-xl px-4 py-3 text-sm">
          <AlertTriangle size={16} className="shrink-0" />
          <span><strong>{stats.overdue_unassigned} iş</strong> planlanmış tarihini geçmiş ve hâlâ atanmamış.</span>
        </div>
      )}
      {stats.overdue_assigned > 0 && (
        <div className="mb-4 flex items-center gap-2 bg-yellow-50 border border-yellow-200 text-yellow-700 rounded-xl px-4 py-3 text-sm">
          <AlertTriangle size={16} className="shrink-0" />
          <span><strong>{stats.overdue_assigned} iş</strong> atanmış ancak planlanmış tarihini geçmiş.</span>
        </div>
      )}

      {/* Stat Kartları */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
        <StatCard icon={Briefcase}    label="Toplam İş"     value={stats.total}     color="bg-blue-500"   onClick={() => navigate('/jobs')} />
        <StatCard icon={TrendingUp}   label="Aktif İş"      value={stats.active}    color="bg-orange-500" onClick={() => navigate('/jobs?filter=active')} />
        <StatCard icon={CheckCircle}  label="Tamamlanan"    value={stats.completed} color="bg-green-500"  onClick={() => navigate('/jobs?filter=completed')} />
        <StatCard icon={AlertCircle}  label="Acil İş"       value={stats.urgent}    color="bg-red-500"    sub="bekliyor" onClick={() => navigate('/jobs?filter=urgent')} />
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <StatCard icon={CheckCircle}  label="Bugün Tamamlanan" value={stats.today_completed} color="bg-emerald-500" onClick={() => navigate('/jobs?filter=today_completed')} />
        <StatCard icon={Clock}        label="Bekleyen"      value={stats.pending}   color="bg-slate-500"  onClick={() => navigate('/jobs?filter=pending')} />
        <StatCard icon={Users}        label="En Aktif Personel" value={topStaff ? topStaff.name.split(' ')[0] : '—'} color="bg-purple-500" sub={topStaff ? `${topStaff.count} iş` : ''} onClick={() => navigate('/users')} />
        <StatCard icon={AlertTriangle} label="Gecikmiş"     value={overdueTotal} color="bg-red-400" sub="tarihi geçmiş" onClick={() => navigate('/jobs?filter=overdue')} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Aktif İşler */}
        <div className="lg:col-span-2 bg-white rounded-xl border border-slate-100 shadow-sm">
          <div className="px-5 py-4 border-b flex items-center justify-between">
            <h2 className="font-semibold text-slate-800">Aktif İşler</h2>
            <span className="text-xs bg-orange-100 text-orange-600 px-2 py-0.5 rounded-full font-medium">
              {stats.active} iş
            </span>
          </div>
          <div className="divide-y">
            {activeList.length === 0 && (
              <p className="px-5 py-8 text-center text-slate-400 text-sm">Aktif iş yok</p>
            )}
            {activeList.map(job => (
              <div
                key={job.id}
                onClick={() => openDetail(job)}
                className="px-5 py-3 flex items-center gap-3 cursor-pointer hover:bg-slate-50 transition-colors"
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5">
                    {job.job_number && (
                      <span className="text-xs font-mono text-slate-400 shrink-0">{formatJobNumber(job.job_number)}</span>
                    )}
                    <p className="font-medium text-slate-800 text-sm truncate">{job.title}</p>
                  </div>
                  <p className="text-xs text-slate-500 truncate">
                    {[job.assigned_to_name || 'Atanmamış', job.customer_name].filter(Boolean).join(' · ')}
                  </p>
                </div>
                <div className="flex gap-2 shrink-0">
                  <PriorityBadge priority={job.priority} />
                  <StatusBadge status={job.status} />
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Özet */}
        <div className="space-y-4">
          {/* Durum Dağılımı */}
          <div className="bg-white rounded-xl border border-slate-100 shadow-sm p-5">
            <h2 className="font-semibold text-slate-800 mb-4">Durum Dağılımı</h2>
            <div className="space-y-2.5">
              {[
                { label: 'Bekliyor',   value: stats.pending,   color: 'bg-slate-400', max: stats.total },
                { label: 'Aktif',      value: stats.active,    color: 'bg-orange-500', max: stats.total },
                { label: 'Tamamlandı', value: stats.completed, color: 'bg-green-500',  max: stats.total },
              ].map(item => (
                <div key={item.label}>
                  <div className="flex justify-between text-xs text-slate-600 mb-1">
                    <span>{item.label}</span>
                    <span className="font-medium">{item.value}</span>
                  </div>
                  <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                    <div
                      className={`h-full rounded-full ${item.color} transition-all`}
                      style={{ width: item.max ? `${(item.value / item.max) * 100}%` : '0%' }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Personel */}
          <div className="bg-white rounded-xl border border-slate-100 shadow-sm p-5">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-purple-100 rounded-lg">
                <Users size={20} className="text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-800">{staffCount}</p>
                <p className="text-sm text-slate-500">Aktif Personel</p>
              </div>
            </div>
          </div>

          {/* Son Eklenen */}
          <div className="bg-white rounded-xl border border-slate-100 shadow-sm">
            <div className="px-5 py-4 border-b">
              <h2 className="font-semibold text-slate-800 text-sm">Son Eklenen</h2>
            </div>
            <div className="divide-y">
              {recentList.slice(0, 5).map(job => (
                <div key={job.id} className="px-5 py-2.5 flex items-center gap-2">
                  <StatusBadge status={job.status} />
                  <p className="text-xs text-slate-700 truncate flex-1">{job.title}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* İş Detay Modal */}
      <Modal
        open={!!detailJob}
        onClose={() => setDetailJob(null)}
        title={detailJob?.title || ''}
        size="lg"
      >
        {detailLoading ? (
          <div className="flex justify-center py-8">
            <div className="animate-spin w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full" />
          </div>
        ) : detailJob ? (
          <JobDetail
            job={detailJob}
            onClose={() => setDetailJob(null)}
            onEdit={(job) => { setEditJob(job); setDetailJob(null); }}
            onRefresh={() => { setDetailJob(null); loadDashboard(); }}
          />
        ) : null}
      </Modal>

      {/* Düzenle Modal */}
      <Modal open={!!editJob} onClose={() => setEditJob(null)} title="İşi Düzenle" size="lg">
        {editJob && (
          <JobForm
            job={editJob}
            onSuccess={() => { setEditJob(null); loadDashboard(); }}
            onCancel={() => setEditJob(null)}
          />
        )}
      </Modal>
    </div>
  );
}
